import { MenuItem, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

import type { StoreLocationResponse } from '../../model/settingsTypes';

export interface RetailEvotorLocationSelectProps {
  /** The bound location's id, or `''` when the store is not bound to one. */
  value: string;
  onChange: (locationId: string) => void;
  locations: StoreLocationResponse[];
  label: string;
  /** Shown as the value=`''` placeholder item (e.g. "Not bound"). */
  placeholderLabel: string;
  /** Appended to an inactive location's option text (e.g. "— inactive"). */
  inactiveSuffix: string;
  disabled?: boolean;
  size?: TextFieldProps['size'];
  sx?: TextFieldProps['sx'];
  helperText?: string;
}

/**
 * Evotor-store → OBZR-location bind control (plan-integration-010 Phase 6,
 * user-story-integration-024). A small, **store-settings-owned** copy of `features/inventory`'s
 * `LocationSelect` pattern — FSD forbids importing a UI component across features, and the two
 * pickers serve different call sites (`StoreLocationResponse` from this feature's own
 * `storeServiceApi`/`useStoreLocations`, vs inventory's separate `InventoryLocationSummary`
 * lookup) even though both ultimately read `POST /api/v1/store/locations/search`.
 *
 * Inactive locations stay listed — so a store's current (possibly since-deactivated) binding
 * still renders correctly, matching `notExportingReason: 'LOCATION_INACTIVE'` — but are disabled
 * as new bind targets, per AC1's "binding requires an... active location".
 */
export function RetailEvotorLocationSelect({
  value,
  onChange,
  locations,
  label,
  placeholderLabel,
  inactiveSuffix,
  disabled,
  size = 'small',
  sx,
  helperText,
}: RetailEvotorLocationSelectProps) {
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      size={size}
      sx={sx}
      helperText={helperText}
      slotProps={{ select: { displayEmpty: true } }}
    >
      <MenuItem value="">
        <em>{placeholderLabel}</em>
      </MenuItem>
      {locations.map((location) => (
        <MenuItem key={location.id} value={location.id} disabled={!location.active}>
          {location.name} ({location.code})
          {!location.active ? ` — ${inactiveSuffix}` : ''}
        </MenuItem>
      ))}
    </TextField>
  );
}
